import type { ReactNode } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/admin/coaching/coaching-dashboard/shared/tooltip'

interface AssignmentActionsProps {
  onAdd: () => void
  onTray: () => void
  addLabel?: string
  trayLabel?: string
  variant: 'compact' | 'standard'
  onTooltipOpenChange?: (open: boolean) => void
  disabled?: boolean
  // Shown in both tooltips instead of the action help while disabled, so
  // the row can explain *why* staging is unavailable (e.g. already in the
  // plan).
  disabledHelp?: string
  // False removes the pair from the page tab order (tabIndex -1) while
  // keeping them clickable and programmatically focusable. The Sources tree
  // roves its tabindex (LG-312): only the focused row's actions may sit in
  // the tab order, or every row re-becomes the multi-stop tab gauntlet the
  // roving pattern exists to remove.
  tabbable?: boolean
}

const ACTION_HELP = {
  add: 'Auto-schedule on the first day with room',
  tray: 'Stage in the tray',
} as const

export function AssignmentActions(props: AssignmentActionsProps): ReactNode {
  const {
    onAdd,
    onTray,
    addLabel,
    trayLabel,
    variant,
    onTooltipOpenChange,
    disabled = false,
    disabledHelp,
    tabbable = true,
  } = props
  const addHelp = disabled && disabledHelp ? disabledHelp : ACTION_HELP.add
  const trayHelp = disabled && disabledHelp ? disabledHelp : ACTION_HELP.tray
  const compact = variant === 'compact'
  const baseClass = compact
    ? 'whitespace-nowrap py-0.5 text-[9.5px] font-bold'
    : 'whitespace-nowrap rounded-[6px] py-0.5 text-[10px] font-bold'

  return (
    <span className="flex shrink-0 gap-1">
      <Tooltip onOpenChange={onTooltipOpenChange}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={addLabel}
            onClick={onAdd}
            disabled={disabled}
            tabIndex={tabbable ? undefined : -1}
            className={`${baseClass} border-0 bg-(--cd-green) text-(--cd-bg) hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:opacity-45 ${compact ? 'rounded-[5px] px-2' : 'px-[9px]'}`}
          >
            + Add
          </button>
        </TooltipTrigger>
        <TooltipContent>{addHelp}</TooltipContent>
      </Tooltip>
      <Tooltip onOpenChange={onTooltipOpenChange}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={trayLabel}
            onClick={onTray}
            disabled={disabled}
            tabIndex={tabbable ? undefined : -1}
            className={`${baseClass} border border-(--cd-border-2) bg-transparent text-(--cd-text-muted) hover:text-(--cd-text) disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:text-(--cd-text-muted) ${compact ? 'rounded-[5px] px-[7px]' : 'px-[9px]'}`}
          >
            Tray
          </button>
        </TooltipTrigger>
        <TooltipContent>{trayHelp}</TooltipContent>
      </Tooltip>
    </span>
  )
}
